import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  getLandingPages,
  LandingPage,
  deleteLandingPage,
  archiveLandingPage,
  unarchiveLandingPage,
  getLeadSubmissions,
  getLandingPage,
  getLandingPageComponents,
  getProject,
} from '../services/api';
import LPComponentRenderer, { BrandTheme } from './LandingPage/LPComponentRenderer';
import LandingPageCampaignModal from './LandingPageCampaignModal';
import { GlassCard } from './ui/GlassCard';
import { GlassButton } from './ui/GlassButton';
import { GlassBadge } from './ui/GlassBadge';
import { ModuleHeader } from './ui/ModuleHeader';
import { Plus, Eye, ExternalLink, RotateCw, Users, Archive, Trash2, Undo2, Settings, X, Cloud, CloudOff, Copy, CheckCheck, Pencil, Rocket, Megaphone } from 'lucide-react';
import { updateLandingPage, deployLandingPage, undeployLandingPage, getCustomDomains, apiClient, duplicateLandingPage } from '../services/api';
import './LandingPagesList.css';

interface LandingPagesListProps {
  onClose?: () => void;
  projectId?: string;
}

const LandingPagesList: React.FC<LandingPagesListProps> = ({
  onClose,
  projectId
}) => {
  const ctx = useOutletContext<{ selectedProjectId?: string }>();
  const effectiveProjectId = projectId || ctx?.selectedProjectId;
  const navigate = useNavigate();
  const [landingPages, setLandingPages] = useState<LandingPage[]>([]);
  const [leadCounts, setLeadCounts] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState<string>('');
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [captureData, setCaptureData] = useState<{ components: any[]; title: string; brandTheme?: BrandTheme } | null>(null);
  const captureRef = useRef<HTMLDivElement>(null);
  const [seoSettingsPage, setSeoSettingsPage] = useState<LandingPage | null>(null);
  const [campaignModalPage, setCampaignModalPage] = useState<LandingPage | null>(null);
  const [seoForm, setSeoForm] = useState({
    slug: '',
    meta_title: '', meta_description: '',
    og_title: '', og_description: '', og_image: '', canonical_url: '',
    analytics_code: '', cookie_consent_enabled: false, noindex: false,
  });
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [deployingPageId, setDeployingPageId] = useState<string | null>(null);
  const [duplicatingPageId, setDuplicatingPageId] = useState<string | null>(null);
  const [copiedDeployUrl, setCopiedDeployUrl] = useState<string | null>(null);
  const [domainsByProject, setDomainsByProject] = useState<Record<string, string>>({});
  const [slugsByProject, setSlugsByProject] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!effectiveProjectId) return;
    loadLandingPages();
  }, [filter, effectiveProjectId]);

  const loadLandingPages = async () => {
    setIsLoading(true);
    try {
      const isArchived = filter === 'archived';
      const status = (!isArchived && filter !== 'all') ? filter : undefined;
      const data = await getLandingPages(effectiveProjectId, status, isArchived);
      setLandingPages(data);

      // Load lead counts for each landing page
      const counts: Record<string, number> = {};
      await Promise.all(
        data.map(async (page) => {
          try {
            const leads = await getLeadSubmissions(undefined, page.landing_page_id);
            counts[page.landing_page_id] = leads.length;
          } catch (err) {
            counts[page.landing_page_id] = 0;
          }
        })
      );
      setLeadCounts(counts);

      // Load verified custom domains for all projects present in the page list
      const uniqueProjectIds = Array.from(new Set(data.map(p => p.project_id).filter(Boolean)));
      const domainMap: Record<string, string> = {};
      await Promise.all(
        uniqueProjectIds.map(async (pid) => {
          try {
            const domains = await getCustomDomains(pid);
            // Prefer is_primary, then first active/dns_verified
            const primary = domains.find((d: any) => d.is_primary && (d.status === 'active' || d.status === 'dns_verified'));
            const fallback = domains.find((d: any) => d.status === 'active' || d.status === 'dns_verified');
            const best = primary || fallback;
            if (best) domainMap[pid] = best.domain;
          } catch { /* ignore per-project failures */ }
        })
      );
      setDomainsByProject(domainMap);

      // Load project slugs for scoped landing page URLs
      const slugMap: Record<string, string> = {};
      await Promise.all(
        uniqueProjectIds.map(async (pid) => {
          try {
            const proj = await getProject(pid);
            if (proj.slug) slugMap[pid] = proj.slug;
          } catch { /* ignore */ }
        })
      );
      setSlugsByProject(slugMap);
    } catch (err) {
      console.error('Failed to load landing pages:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePublish = (landingPageId: string) => {
    // Navigate to editor — publishing must go through the editor
    // so the component-based HTML is generated correctly
    navigate(`/landing-pages/${landingPageId}/edit`);
  };

  const handleUpdate = (page: LandingPage) => {
    // Navigate to editor for re-publish — ensures component HTML is regenerated
    navigate(`/landing-pages/${page.landing_page_id}/edit`);
  };

  const handleArchive = async (page: LandingPage) => {
    const msg = page.status === 'published'
      ? 'Archive this landing page? It will be taken offline and the URL will no longer work.'
      : 'Archive this landing page?';
    if (!window.confirm(msg)) return;

    try {
      await archiveLandingPage(page.landing_page_id);
      loadLandingPages();
    } catch (err) {
      alert('Failed to archive landing page');
    }
  };

  const handleRestore = async (landingPageId: string) => {
    try {
      await unarchiveLandingPage(landingPageId);
      loadLandingPages();
    } catch (err) {
      alert('Failed to restore landing page');
    }
  };

  const handleDelete = async (landingPageId: string) => {
    if (!window.confirm('Permanently delete this landing page? This action cannot be undone.')) return;

    try {
      await deleteLandingPage(landingPageId);
      loadLandingPages();
    } catch (err) {
      alert('Failed to delete landing page');
    }
  };

  const handleDuplicate = async (page: LandingPage) => {
    if (!window.confirm(`Duplicate "${page.title}"? A new draft will be created with "(Copy)" appended to the title.`)) return;
    setDuplicatingPageId(page.landing_page_id);
    try {
      await duplicateLandingPage(page.landing_page_id);
      loadLandingPages();
    } catch (err) {
      alert('Failed to duplicate landing page');
    } finally {
      setDuplicatingPageId(null);
    }
  };

  const handlePreview = async (page: LandingPage) => {
    setIsLoadingPreview(true);
    try {
      const [components, fullPage] = await Promise.all([
        getLandingPageComponents(page.landing_page_id),
        getLandingPage(page.landing_page_id),
      ]);
      if (!components.length) {
        alert('No preview available yet — open this page in the editor first.');
        return;
      }

      // Build brand theme — mirror editor logic
      const tc = (fullPage as any).theme_config || {};
      let brandTheme: BrandTheme = {
        primaryColor: tc.brand_primary_color || tc.primary_color || '#2177FF',
        secondaryColor: tc.brand_secondary_color || '#1EACA6',
        fontFamily: tc.brand_primary_font || 'inherit',
        logoUrl: tc.logo_url || '',
        companyName: tc.company_name || '',
      };
      const pid = fullPage.project_id;
      if (pid && pid !== 'unassigned') {
        try {
          let bg: any = null;
          try {
            const r = await apiClient.get(`/brand-guides/project/${pid}/default`);
            bg = r.data;
          } catch {
            const r2 = await apiClient.get(`/brand-guides/project/${pid}`);
            if (r2.data?.length > 0) bg = r2.data[0];
          }
          if (bg) {
            brandTheme = {
              primaryColor: bg.primary_color || brandTheme.primaryColor,
              secondaryColor: bg.secondary_color || brandTheme.secondaryColor,
              accentColor: bg.accent_color || undefined,
              fontFamily: bg.primary_font || bg.secondary_font || brandTheme.fontFamily,
              logoUrl: bg.logo_url || brandTheme.logoUrl,
              companyName: bg.brand_name || brandTheme.companyName,
            };
          }
        } catch { /* keep defaults */ }
      }

      setCaptureData({ components, title: page.title, brandTheme });
    } catch {
      alert('Failed to load preview.');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // After captureData renders into the hidden div, grab the HTML and POST to server for preview
  useEffect(() => {
    if (!captureData || !captureRef.current) return;
    const timer = setTimeout(async () => {
      const div = captureRef.current;
      if (!div) return;
      const origin = window.location.origin;
      let inner = div.innerHTML;
      // Replace quiz embed iframes with inline <aime-quiz> web components
      inner = inner.replace(
        /<iframe[^>]+src=["'][^"']*\/quiz-embed\/([^?'"]+)\?([^"']*)["'][^>]*><\/iframe>/g,
        (_match, quizId, queryString) => {
          const qp = new URLSearchParams(queryString.replace(/&amp;/g, '&'));
          const pc = qp.get('primary_color') || '#2177FF';
          const sc = qp.get('secondary_color') || '';
          const ff = qp.get('font_family') || '';
          return `<aime-quiz quiz-id="${quizId}" primary-color="${pc}"${sc ? ` secondary-color="${sc}"` : ''}${ff ? ` font-family="${ff}"` : ''} style="display:block;max-width:680px;margin:0 auto"></aime-quiz>`;
        }
      );
      const btnHoverCss = `html{scroll-behavior:smooth}.lp-btn{transition:filter .18s ease,transform .18s ease;cursor:pointer}.lp-btn:hover{filter:brightness(.88);transform:translateY(-1px) scale(1.02)}.lp-btn:active{filter:brightness(.78);transform:translateY(0) scale(.99)}.lp-btn-ghost{transition:background .18s ease,transform .18s ease;cursor:pointer}.lp-btn-ghost:hover{background:rgba(255,255,255,.28)!important;transform:translateY(-1px)}.lp-btn-ghost:active{transform:translateY(0)}`;
      const font = captureData.brandTheme?.fontFamily;
      const systemFonts = ['inherit', 'sans-serif', 'serif', 'monospace', '-apple-system'];
      const isCustomFont = font && !systemFonts.some(sf => font.toLowerCase().includes(sf));
      const fontLink = isCustomFont
        ? `<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=${encodeURIComponent(font!)}:wght@400;500;600;700;800&display=swap">`
        : '';
      const fontFamily = font && font !== 'inherit' ? `'${font}', ` : '';
      const fullHtml = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${captureData.title}</title>${fontLink}<link rel="stylesheet" href="${origin}/widgets/style.css"><style>body{margin:0;background:#ffffff;font-family:${fontFamily}-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif}${btnHoverCss}</style></head><body>${inner}<script type="module" src="${origin}/widgets/aime-widgets.js"></script></body></html>`;
      try {
        const res = await fetch('/api/landing-pages/preview-temp', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ html: fullHtml }),
        });
        const { uid } = await res.json();
        setPreviewTitle(captureData.title);
        setPreviewUrl(`/api/landing-pages/preview-temp/${uid}`);
      } catch {
        // Fallback to blob if server unavailable
        const blob = new Blob([fullHtml], { type: 'text/html' });
        setPreviewTitle(captureData.title);
        setPreviewUrl(URL.createObjectURL(blob));
      }
      setCaptureData(null);
    }, 200);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captureData]);

  const closePreview = () => {
    if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const openSeoSettings = (page: LandingPage) => {
    setSeoSettingsPage(page);
    setSeoForm({
      slug: page.slug || '',
      meta_title: page.meta_title || '',
      meta_description: page.meta_description || '',
      og_title: page.og_title || '',
      og_description: page.og_description || '',
      og_image: page.og_image || '',
      canonical_url: page.canonical_url || '',
      analytics_code: page.analytics_code || '',
      cookie_consent_enabled: page.cookie_consent_enabled || false,
      noindex: page.noindex || false,
    });
  };

  const handleDeploy = async (page: LandingPage) => {
    if (!window.confirm(`Deploy "${page.title}" to Cloudflare Pages? It will be publicly accessible at a cloudflare.pages.dev URL.`)) return;
    setDeployingPageId(page.landing_page_id);
    try {
      const result = await deployLandingPage(page.landing_page_id);
      setLandingPages(prev => prev.map(p =>
        p.landing_page_id === page.landing_page_id
          ? { ...p, deployed_url: result.deployed_url, deploy_status: result.deploy_status }
          : p
      ));
    } catch (err) {
      alert('Failed to deploy landing page');
    } finally {
      setDeployingPageId(null);
    }
  };

  const handleUndeploy = async (page: LandingPage) => {
    if (!window.confirm(`Take "${page.title}" offline? The deployed URL will stop working.`)) return;
    try {
      await undeployLandingPage(page.landing_page_id);
      setLandingPages(prev => prev.map(p =>
        p.landing_page_id === page.landing_page_id
          ? { ...p, deploy_status: 'offline', deployed_url: undefined }
          : p
      ));
    } catch (err) {
      alert('Failed to take page offline');
    }
  };

  const handleCopyDeployUrl = (url: string, pageId: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedDeployUrl(pageId);
      setTimeout(() => setCopiedDeployUrl(null), 2000);
    });
  };

  const getDeployBadgeVariant = (deployStatus?: string): 'success' | 'warning' | 'danger' | 'default' => {
    switch (deployStatus) {
      case 'live': return 'success';
      case 'offline': return 'danger';
      case 'not_deployed': return 'default';
      default: return 'default';
    }
  };

  const saveSeoSettings = async () => {
    if (!seoSettingsPage) return;
    setIsSavingSettings(true);
    try {
      // Only send the slug when it actually changed
      const { slug, ...rest } = seoForm;
      const payload = slug && slug !== seoSettingsPage.slug ? { ...rest, slug } : rest;
      const updated = await updateLandingPage(seoSettingsPage.landing_page_id, payload);
      setLandingPages(prev => prev.map(p =>
        p.landing_page_id === updated.landing_page_id ? updated : p
      ));
      setSeoSettingsPage(null);
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to save settings');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const getStatusBadgeVariant = (status: string): 'success' | 'warning' | 'danger' | 'default' => {
    switch (status) {
      case 'published':
        return 'success';
      case 'draft':
        return 'warning';
      case 'archived':
        return 'danger';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="landing-pages-list">
        <div className="loading">Loading landing pages...</div>
      </div>
    );
  }

  return (
    <div className="landing-pages-list">
      <ModuleHeader
        title="Landing Pages"
        breadcrumb={['Home', 'Landing Pages']}
        actions={[
          <GlassButton 
            key="create" 
            variant="primary" 
            onClick={() => navigate('/landing-pages/create')}
            icon={<Plus size={18} />}
          >
            Create Landing Page
          </GlassButton>
        ]}
      />

      <GlassCard variant="default" padding="md" className="filters-card">
        <div className="list-filters">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All Active
          </button>
          <button
            className={`filter-btn ${filter === 'draft' ? 'active' : ''}`}
            onClick={() => setFilter('draft')}
          >
            Drafts
          </button>
          <button
            className={`filter-btn ${filter === 'published' ? 'active' : ''}`}
            onClick={() => setFilter('published')}
          >
            Published
          </button>
          <button
            className={`filter-btn ${filter === 'archived' ? 'active' : ''}`}
            onClick={() => setFilter('archived')}
          >
            Archived
          </button>
        </div>
      </GlassCard>

      {!effectiveProjectId ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
          <Rocket size={64} style={{ opacity: 0.4, marginBottom: '16px' }} />
          <h3 style={{ color: 'var(--text)', marginBottom: '8px' }}>Select a project</h3>
          <p>Choose a project from the sidebar to view its landing pages</p>
        </div>
      ) : landingPages.length === 0 ? (
        <GlassCard variant="default" padding="lg" className="empty-state-card">
          <div className="empty-state">
            {filter === 'archived' ? (
              <>
                <h2>No archived pages</h2>
                <p>Pages you archive will appear here.</p>
              </>
            ) : (
              <>
                <h2>No landing pages yet</h2>
                <p>Create your first AI-generated landing page to start capturing leads.</p>
                <GlassButton 
                  variant="primary" 
                  onClick={() => navigate('/landing-pages/create')}
                  icon={<Plus size={18} />}
                >
                  Create Landing Page
                </GlassButton>
              </>
            )}
          </div>
        </GlassCard>
      ) : (
        <div className="pages-grid">
          {landingPages.map(page => (
            <GlassCard 
              key={page.landing_page_id} 
              variant="hover" 
              padding="md"
              className={`page-card${page.status === 'archived' ? ' page-card--archived' : ''}`}
            >
              <div className="card-header">
                <h3>{page.title}</h3>
                <GlassBadge variant={getStatusBadgeVariant(page.status)}>
                  {page.status}
                </GlassBadge>
              </div>

              <div className="card-meta">
                <span>Created: {formatDate(page.created_at)}</span>
                {page.published_at && (
                  <span>Published: {formatDate(page.published_at)}</span>
                )}
              </div>

              <div className="card-stats">
                <div className="stat">
                  <span className="stat-label">Leads Captured</span>
                  <span className="stat-value">{leadCounts[page.landing_page_id] || 0}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Model</span>
                  <span className="stat-value">{page.llm_model || 'N/A'}</span>
                </div>
              </div>

              {/* H5.3 Cloudflare Pages deploy status */}
              {page.status !== 'archived' && (
                <div className="card-deploy-row">
                  <GlassBadge variant={getDeployBadgeVariant(page.deploy_status)}>
                    {page.deploy_status === 'live' ? 'Live on CF Pages' : page.deploy_status === 'offline' ? 'Offline' : 'Not Deployed'}
                  </GlassBadge>
                  {page.deploy_status === 'live' && page.deployed_url && (
                    <>
                      <a
                        href={page.deployed_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="deploy-url-link"
                        title={page.deployed_url}
                      >
                        <ExternalLink size={13} />
                        <span>{page.deployed_url.replace('https://', '').slice(0, 36)}{page.deployed_url.length > 42 ? '…' : ''}</span>
                      </a>
                      <GlassButton
                        variant="ghost"
                        size="sm"
                        icon={copiedDeployUrl === page.landing_page_id ? <CheckCheck size={13} /> : <Copy size={13} />}
                        onClick={() => handleCopyDeployUrl(page.deployed_url!, page.landing_page_id)}
                        title="Copy deployed URL"
                      />
                    </>
                  )}
                </div>
              )}

              <div className="card-actions">
                {page.status !== 'archived' && (
                  <GlassButton
                    variant="ghost"
                    size="sm"
                    onClick={() => handlePreview(page)}
                    disabled={isLoadingPreview}
                    icon={<Eye size={16} />}
                  >
                    Preview
                  </GlassButton>
                )}

                {page.status !== 'archived' && (
                  <GlassButton
                    variant="secondary"
                    size="sm"
                    onClick={() => navigate(`/landing-pages/${page.landing_page_id}/edit`)}
                    icon={<Pencil size={16} />}
                  >
                    Edit
                  </GlassButton>
                )}

                {page.status !== 'archived' && (
                  <GlassButton
                    variant="secondary"
                    size="sm"
                    loading={duplicatingPageId === page.landing_page_id}
                    onClick={() => handleDuplicate(page)}
                    icon={<Copy size={16} />}
                  >
                    Duplicate
                  </GlassButton>
                )}

                {page.status !== 'archived' && page.deploy_status !== 'live' && (
                  <GlassButton
                    variant="secondary"
                    size="sm"
                    loading={deployingPageId === page.landing_page_id}
                    onClick={() => handleDeploy(page)}
                    icon={<Cloud size={16} />}
                  >
                    Deploy to Web
                  </GlassButton>
                )}

                {page.status !== 'archived' && page.deploy_status === 'live' && (
                  <GlassButton
                    variant="ghost"
                    size="sm"
                    onClick={() => handleUndeploy(page)}
                    icon={<CloudOff size={16} />}
                  >
                    Take Offline
                  </GlassButton>
                )}

                {page.status === 'published' && (
                  <>
                    {needsUpdate(page) ? (
                      <GlassButton
                        variant="secondary"
                        size="sm"
                        onClick={() => handleUpdate(page)}
                        title="Database content is newer than live page. Click to update live site."
                        icon={<RotateCw size={16} />}
                      >
                        Update
                      </GlassButton>
                    ) : (
                      <GlassButton
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const domain = domainsByProject[page.project_id];
                          const projSlug = slugsByProject[page.project_id];
                          const pagePath = projSlug
                            ? `${projSlug}/${page.slug}`
                            : page.slug;
                          const url = domain
                            ? `https://${domain}/${page.slug}/?t=${Date.now()}`
                            : `/pages/${pagePath}/?t=${Date.now()}`;
                          window.open(url, '_blank');
                        }}
                        icon={<ExternalLink size={16} />}
                      >
                        View Live
                      </GlassButton>
                    )}
                  </>
                )}

                {page.status === 'draft' && (
                  <GlassButton
                    variant="primary"
                    size="sm"
                    onClick={() => handlePublish(page.landing_page_id)}
                  >
                    Publish
                  </GlassButton>
                )}

                {page.status !== 'archived' && (
                  <GlassButton
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/landing-pages/${page.landing_page_id}/leads`)}
                    icon={<Users size={16} />}
                  >
                    Leads ({leadCounts[page.landing_page_id] || 0})
                  </GlassButton>
                )}

                {page.status !== 'archived' && (
                  <GlassButton
                    variant="ghost"
                    size="sm"
                    onClick={() => openSeoSettings(page)}
                    title="SEO & Analytics settings"
                    icon={<Settings size={16} />}
                  >
                    Settings
                  </GlassButton>
                )}

                {page.status !== 'archived' && (
                  <GlassButton
                    variant="ghost"
                    size="sm"
                    onClick={() => setCampaignModalPage(page)}
                    title="Link this page to a campaign"
                    icon={<Megaphone size={16} />}
                  >
                    Campaign
                  </GlassButton>
                )}

                {page.status !== 'archived' ? (
                  <GlassButton
                    variant="ghost"
                    size="sm"
                    onClick={() => handleArchive(page)}
                    title="Archive this page (takes it offline if published)"
                    icon={<Archive size={16} />}
                  >
                    Archive
                  </GlassButton>
                ) : (
                  <>
                    <GlassButton
                      variant="secondary"
                      size="sm"
                      onClick={() => handleRestore(page.landing_page_id)}
                      icon={<Undo2 size={16} />}
                    >
                      Restore
                    </GlassButton>
                    <GlassButton
                      variant="danger"
                      size="sm"
                      onClick={() => handleDelete(page.landing_page_id)}
                      icon={<Trash2 size={16} />}
                    >
                      Delete
                    </GlassButton>
                  </>
                )}
              </div>
            </GlassCard>
          ))}
        </div>
      )}

      {/* ── Off-screen capture div (renders LP sections to grab innerHTML) ── */}
      {captureData && (
        <div ref={captureRef} style={{ position: 'fixed', left: '-9999px', top: 0, width: '1200px', visibility: 'hidden', pointerEvents: 'none', zIndex: -1 }}>
          {captureData.components.map((comp: any) => (
            <LPComponentRenderer key={comp.id} component={comp} isSelected={false} brandTheme={captureData.brandTheme} />
          ))}
        </div>
      )}

      {/* ── LP Preview floating modal ──────────────────────────────────── */}
      {previewUrl && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={closePreview}
        >
          <div
            style={{ width: '82%', maxWidth: '1100px', height: '88vh', display: 'flex', flexDirection: 'column', background: 'var(--panel)', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 24px 80px rgba(0,0,0,0.45)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid var(--stroke)', flexShrink: 0 }}>
              <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text)' }}>Preview — {previewTitle}</span>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button onClick={() => window.open(previewUrl!, '_blank')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  <ExternalLink size={14} /> Open in new tab
                </button>
                <button onClick={closePreview} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', padding: '4px' }} title="Close">
                  <X size={18} />
                </button>
              </div>
            </div>
            <iframe src={previewUrl} style={{ flex: 1, width: '100%', border: 'none', display: 'block' }} title={`Preview — ${previewTitle}`} />
          </div>
        </div>
      )}

      {seoSettingsPage && (
        <div className="seo-modal-overlay" onClick={() => setSeoSettingsPage(null)}>
          <div className="seo-modal" onClick={e => e.stopPropagation()}>
            <div className="seo-modal-header">
              <h2>SEO &amp; Analytics — {seoSettingsPage.title}</h2>
              <button className="seo-modal-close" onClick={() => setSeoSettingsPage(null)} title="Close">
                <X size={18} />
              </button>
            </div>
            <div className="seo-modal-body">
              <div className="seo-field-group">
                <label>URL Slug</label>
                <input
                  type="text"
                  value={seoForm.slug}
                  onChange={e => setSeoForm(f => ({ ...f, slug: e.target.value }))}
                  placeholder="my-page-name"
                />
                <small style={{ color: 'var(--text-secondary)' }}>
                  Live URL: {domainsByProject[seoSettingsPage.project_id]
                    ? `https://${domainsByProject[seoSettingsPage.project_id]}/`
                    : `/pages/${slugsByProject[seoSettingsPage.project_id] ? slugsByProject[seoSettingsPage.project_id] + '/' : ''}`}
                  {(seoForm.slug || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}
                  {seoSettingsPage.status === 'published' && seoForm.slug !== seoSettingsPage.slug
                    ? ' — the old URL will stop working; re-publish to update the canonical tag in the page.'
                    : ''}
                </small>
              </div>
              <div className="seo-field-group">
                <label>Meta Title</label>
                <input
                  type="text"
                  value={seoForm.meta_title}
                  onChange={e => setSeoForm(f => ({ ...f, meta_title: e.target.value }))}
                  placeholder="Search-result title (defaults to page title)"
                />
              </div>
              <div className="seo-field-group">
                <label>Meta Description</label>
                <textarea
                  value={seoForm.meta_description}
                  onChange={e => setSeoForm(f => ({ ...f, meta_description: e.target.value }))}
                  placeholder="A compelling description that appears in search results…"
                  rows={2}
                />
              </div>
              <div className="seo-field-group">
                <label>OG Title</label>
                <input
                  type="text"
                  value={seoForm.og_title}
                  onChange={e => setSeoForm(f => ({ ...f, og_title: e.target.value }))}
                  placeholder="Social share title (defaults to page title)"
                />
              </div>
              <div className="seo-field-group">
                <label>OG Description</label>
                <textarea
                  value={seoForm.og_description}
                  onChange={e => setSeoForm(f => ({ ...f, og_description: e.target.value }))}
                  placeholder="Social share description"
                  rows={2}
                />
              </div>
              <div className="seo-field-group">
                <label>OG Image URL</label>
                <input
                  type="url"
                  value={seoForm.og_image}
                  onChange={e => setSeoForm(f => ({ ...f, og_image: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
              <div className="seo-field-group">
                <label>Canonical URL</label>
                <input
                  type="url"
                  value={seoForm.canonical_url}
                  onChange={e => setSeoForm(f => ({ ...f, canonical_url: e.target.value }))}
                  placeholder="https://yourdomain.com/page"
                />
              </div>
              <div className="seo-field-group">
                <label>Analytics Code</label>
                <textarea
                  value={seoForm.analytics_code}
                  onChange={e => setSeoForm(f => ({ ...f, analytics_code: e.target.value }))}
                  placeholder="Paste your GA4, Meta Pixel, Plausible, or other tracking snippet here"
                  rows={4}
                  style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                />
              </div>
              <div className="seo-field-group seo-field-group--checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={seoForm.cookie_consent_enabled}
                    onChange={e => setSeoForm(f => ({ ...f, cookie_consent_enabled: e.target.checked }))}
                  />
                  <span>Show cookie consent banner on this page</span>
                </label>
              </div>
              <div className="seo-field-group seo-field-group--checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={seoForm.noindex}
                    onChange={e => setSeoForm(f => ({ ...f, noindex: e.target.checked }))}
                  />
                  <span>Hide from search engines (noindex)</span>
                </label>
              </div>
            </div>
            <div className="seo-modal-footer">
              <GlassButton
                variant="primary"
                onClick={saveSeoSettings}
                disabled={isSavingSettings}
                loading={isSavingSettings}
              >
                {isSavingSettings ? 'Saving…' : 'Save Settings'}
              </GlassButton>
              <GlassButton variant="secondary" onClick={() => setSeoSettingsPage(null)} disabled={isSavingSettings}>
                Cancel
              </GlassButton>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginLeft: 8 }}>
                Re-publish this page for changes to take effect.
              </span>
            </div>
          </div>
        </div>
      )}

      {campaignModalPage && (
        <LandingPageCampaignModal
          page={campaignModalPage}
          onClose={() => setCampaignModalPage(null)}
          onSaved={(updated) => {
            setLandingPages(prev => prev.map(p =>
              p.landing_page_id === updated.landing_page_id ? updated : p
            ));
          }}
        />
      )}
    </div>
  );
};

export default LandingPagesList;

// Helper to determine if a published page needs an update
const needsUpdate = (page: LandingPage) => {
  if (page.status !== 'published' || !page.published_at) return false;
  // Add a 2 second buffer to avoid precision/sync issues
  return new Date(page.updated_at).getTime() > (new Date(page.published_at).getTime() + 2000);
};
